import { formatMoneyMinor } from "@repo/vihangam-yog-shared";
import { useEffect, useMemo, useState } from "react";
import { encode } from "uqr";

import { Page } from "../../layout/Page";
import { requestErrorMessage } from "../../requestError";
import { trpc } from "../../trpc";
import { Button } from "../../ui/Button";
import { ContactLine } from "../../ui/ContactLine";
import { Section } from "../../ui/Section";

import { clearPendingPayNow, pendingPayNow } from "./payNowPending";
import { clearDraft } from "./useBundleDraft";

/** How often the page asks whether the bank has paid. */
const POLL_MS = 6000;
const INTENT_RE = /^pi_[A-Za-z0-9]{8,64}$/;
/** Pixels per QR module; 8 gives a crisp code a bank app reads at arm's length. */
const MODULE_PX = 8;

/** The PayNow code as a PNG data URL, or null where there is no canvas. */
function qrPngDataUrl(data: string): string | null {
  const qr = encode(data, { ecc: "M", border: 2 });
  const canvas = document.createElement("canvas");
  canvas.width = qr.size * MODULE_PX;
  canvas.height = qr.size * MODULE_PX;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    return null;
  }
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.fillStyle = "#000000";
  qr.data.forEach((row, y) => {
    row.forEach((dark, x) => {
      if (dark) {
        ctx.fillRect(x * MODULE_PX, y * MODULE_PX, MODULE_PX, MODULE_PX);
      }
    });
  });
  return canvas.toDataURL("image/png");
}

/** The code, drawn here so the payer can hold it, save it and scan it. */
function PayNowCode({
  data,
  fallbackPng,
  fileName,
}: {
  data: string;
  fallbackPng: string | null;
  fileName: string;
}) {
  const png = useMemo(() => qrPngDataUrl(data), [data]);
  const src = png ?? fallbackPng;
  const [saved, setSaved] = useState<string | null>(null);

  async function save() {
    if (!src) {
      return;
    }
    try {
      const blob = await (await fetch(src)).blob();
      const file = new File([blob], fileName, { type: "image/png" });
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], title: "PayNow QR code" });
        setSaved("Saved. Open it from your photos in your bank app.");
        return;
      }
      const a = document.createElement("a");
      a.href = src;
      a.download = fileName;
      a.click();
      setSaved("Saved to your downloads.");
    } catch {
      // The share sheet was dismissed, or the browser refused: nothing
      // to say; the code is still on the screen.
    }
  }

  return (
    <div className="flex flex-col items-center gap-4">
      {src ? (
        <img
          src={src}
          alt="PayNow QR code"
          width={256}
          height={256}
          className="h-64 w-64 rounded-2xl border border-hairline bg-white p-2"
        />
      ) : (
        <p role="alert">We couldn't draw the code on this browser.</p>
      )}
      <Button type="button" onClick={() => void save()} disabled={!src}>
        Save QR code
      </Button>
      <p className="max-w-xs text-center text-xs leading-relaxed text-ink-soft">
        {saved ??
          "On a phone you can also press and hold the code to save it to your photos."}
      </p>
    </div>
  );
}

const STEPS = [
  "Open your bank app and choose Scan or PayNow.",
  "Scan this code, or save it and pick it from your photos.",
  "Check the amount and confirm. This page updates itself once your bank has paid.",
];

export function PayPage() {
  const intent =
    new URLSearchParams(window.location.search).get("intent") ?? "";
  const pending = pendingPayNow();
  const lookup =
    INTENT_RE.test(intent) && pending?.paymentIntentId === intent
      ? pending
      : null;
  const [error, setError] = useState<string | null>(null);
  const status = trpc.payNowStatus.useQuery(
    lookup ?? { paymentIntentId: intent, clientSecret: "" },
    {
      enabled: lookup !== null,
      retry: false,
      refetchInterval: (q) =>
        q.state.data?.state === "pending" ? POLL_MS : false,
    },
  );
  const toCard = trpc.payNowToCard.useMutation();
  const data = status.data;

  useEffect(() => {
    if (data?.state !== "paid") {
      return;
    }
    clearPendingPayNow();
    clearDraft();
    try {
      sessionStorage.setItem("vysg-register-expect-hawan", "1");
    } catch {
      /* storage unavailable; the thanks page just won't poll */
    }
    window.location.href = `/register/thanks?ref=${encodeURIComponent(data.confirmationRef ?? "")}`;
  }, [data]);

  function payByCard() {
    if (!lookup) {
      return;
    }
    setError(null);
    toCard.mutate(lookup, {
      onSuccess: (r) => {
        if (r.checkoutUrl) {
          window.location.href = r.checkoutUrl;
          return;
        }
        if (r.alreadyPaid) {
          void status.refetch();
          return;
        }
        setError(r.error ?? "We couldn't open the card payment.");
      },
      onError: (err) =>
        setError(
          requestErrorMessage(err, "We couldn't open the card payment."),
        ),
    });
  }

  return (
    <Page title="Pay by PayNow" heading="Pay for Sunday by PayNow">
      <Section eyebrow="One step left">
        <div className="max-w-2xl">
          {!lookup ? (
            <div className="grid gap-4">
              <p>
                This payment page only opens on the phone or computer the
                registration was made from. Go back to the form; your details
                are saved there.
              </p>
              <Button href="/register" variant="secondary">
                Back to the form
              </Button>
            </div>
          ) : null}

          {lookup && status.isError ? (
            <div className="grid gap-4" role="alert">
              <p>
                {requestErrorMessage(
                  status.error,
                  "We couldn't find that payment.",
                )}{" "}
                Go back to the form and try again.
              </p>
              <Button href="/register" variant="secondary">
                Back to the form
              </Button>
            </div>
          ) : null}

          {lookup && !status.isError && !data ? (
            <p className="text-ink-soft" role="status">
              Getting your PayNow code…
            </p>
          ) : null}

          {data?.state === "expired" ? (
            <div className="grid gap-4" role="alert">
              <p>
                This code has expired and can no longer be paid. Go back to the
                form and press Register again for a fresh one; your details are
                saved on this device.
              </p>
              <Button href="/register">Back to the form</Button>
            </div>
          ) : null}

          {data?.state === "paid" ? (
            <p role="status">Paid. Taking you to your registration…</p>
          ) : null}

          {data?.state === "pending" ? (
            <div className="grid gap-8">
              <div>
                <p className="text-ink-soft">Amount</p>
                <p className="font-display text-3xl text-ink md:text-4xl">
                  {formatMoneyMinor(data.amountCents, data.currency)}
                </p>
                {data.confirmationRef ? (
                  <p className="mt-1 text-sm text-ink-soft">
                    Reference{" "}
                    <span className="font-mono tracking-wider text-ink">
                      {data.confirmationRef}
                    </span>
                  </p>
                ) : null}
              </div>

              {data.qr ? (
                <div className="grid gap-8 md:grid-cols-[auto_1fr] md:items-start">
                  <PayNowCode
                    data={data.qr.data}
                    fallbackPng={data.qr.pngUrl}
                    fileName={`paynow-${data.confirmationRef ?? "hawan"}.png`}
                  />
                  <ol className="grid gap-3 text-sm leading-relaxed text-ink">
                    {STEPS.map((step, i) => (
                      <li key={step} className="flex gap-3">
                        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-magenta text-xs font-semibold text-white">
                          {i + 1}
                        </span>
                        <span>{step}</span>
                      </li>
                    ))}
                  </ol>
                </div>
              ) : (
                <p role="status">
                  Your bank has sent the payment and Stripe is confirming it.
                  This usually takes a few seconds.
                </p>
              )}

              <p className="text-sm text-ink-soft" role="status">
                Waiting for your payment. Keep this page open, or come back to
                it; the confirmation email arrives either way.
              </p>

              <div className="grid gap-3 border-t border-hairline pt-6">
                <p className="text-sm text-ink">Prefer to pay by card?</p>
                <div className="flex flex-wrap items-center gap-3">
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={payByCard}
                    disabled={toCard.isPending}
                  >
                    {toCard.isPending ? "Opening…" : "Pay by card instead"}
                  </Button>
                  {data.qr?.hostedUrl ? (
                    <Button href={data.qr.hostedUrl} external variant="ghost">
                      Open the code on Stripe's page
                    </Button>
                  ) : null}
                </div>
                {error ? (
                  <p className="text-sm text-crimson" role="alert">
                    {error}
                  </p>
                ) : null}
              </div>
            </div>
          ) : null}

          <div className="mt-8">
            <ContactLine />
          </div>
        </div>
      </Section>
    </Page>
  );
}
